import axios from 'axios';
import { useCallback, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { SOMETHING_WENT_WRONG } from 'constants/common';
import { differenceInSeconds } from 'date-fns';
import { useCustomNavigate } from 'hooks/useCustomNavigate';
import { checkErrorCode, isMantaCareError, isNetworkTimedOut } from 'hooks/useErrorHandler/utils';
import { getConfig, sessionTimerConfig } from '../../config';
import { UNKNOWN_ERROR_OCCURRED } from '../../constants/onboarding';
import {
    ACCOUNT_VERIFIED_ROUTE,
    CAPTURE_PROFILE_ROUTE,
    HOME_ROUTE,
    LOGIN_ROUTE,
    SIGN_UP_ROUTE,
    VERIFICATION_ROUTE
} from '../../constants/routes';
import { UserContext } from '../../context/UserContext';
import { MantaCareUser, OrySession } from '../../context/UserContext/types';
import { createUserSessionFromOrySession, isSessionExpired } from '../../context/UserContext/utils';
import { ory } from '../../ory.config';
import { addSearchParams, getOnboardingRoute, onboardingRoutes } from '../../utils/route';
import { useLogoutFlow } from '../useLogout/useLogoutFlow';
import { useMantaCareAPI } from '../useMantaCareAPI/useMantaCareAPI';
import { useOry } from '../useOry';
import { OryFlowType } from '../useOry/useOry';

/**
 * useAuth is responsible for authenticating routes against user session.
 */
export const useAuth = () => {
    const [loading, setLoading] = useState(true);
    const [refreshingTokens, setRefreshingTokens] = useState(false);
    const [error, setError] = useState('');
    const { handleError } = useOry();
    const { setUserSession, session, user, setMantaCareUser } = useContext(UserContext);
    const { logout } = useLogoutFlow();
    const location = useLocation();
    const { navigateWithSearch, navigateWithRedirectTo } = useCustomNavigate();
    const { getCurrentUser, extendSession } = useMantaCareAPI();

    const toSession = useCallback(async () => {
        const { data } = await ory.toSession({ tokenizeAs: getConfig().ORY_JWT_TOKENIZE_AS });
        const userSession = createUserSessionFromOrySession(data);
        setUserSession(userSession);
        return userSession;
    }, [setUserSession]);

    /**
     * Retrieves the session from the user context if exists otherwise makes an API call to ory
     * to fetch the session and then sets it in the context.
     *
     * @returns {Session}
     */
    const getSession = useCallback(async () => {
        if (session.oryID) {
            return session;
        }
        return toSession();
    }, [session, toSession]);

    const extendUserSession = useCallback(async () => {
        const sessionTimeConfig = sessionTimerConfig();
        const canRefreshSession =
            session.expiredAt &&
            differenceInSeconds(new Date(session.expiredAt), new Date()) <= sessionTimeConfig.refreshSessionBeforeExpiryIn &&
            !refreshingTokens;

        if (canRefreshSession && user) {
            setRefreshingTokens(true);
            await extendSession(user.id, session.jwt);
            await toSession();
            setRefreshingTokens(false);
        }
    }, [session, user, toSession, refreshingTokens]);

    /**
     * handleUseAuthErrors handles auth error.
     */
    const handleUseAuthErrors = useCallback(
        async (e: unknown) => {
            setLoading(false);
            if (!axios.isAxiosError(e)) {
                setError(UNKNOWN_ERROR_OCCURRED);
                return;
            }

            if (isMantaCareError(e)) {
                if (isNetworkTimedOut(e)) {
                    setError(UNKNOWN_ERROR_OCCURRED);
                    return;
                }
                // User not found then navigate to profile route
                if (checkErrorCode(e, 404)) {
                    const pathname = location.pathname === ACCOUNT_VERIFIED_ROUTE ? ACCOUNT_VERIFIED_ROUTE : CAPTURE_PROFILE_ROUTE;
                    navigateWithRedirectTo({ pathname }, { state: location.state });
                    return;
                }
                setError(SOMETHING_WENT_WRONG);
                return;
            }
            handleError(OryFlowType.LOGIN)(e);
        },
        [handleError, navigateWithRedirectTo, location, isMantaCareError]
    );

    /**
     * It acts as a authorization check point. If the session not valid or expired then user redirected to login.
     */
    const authorize = useCallback(async () => {
        try {
            setLoading(true);
            const session = await getSession();
            if (!session.oryID) {
                setLoading(false);
                navigateWithSearch({ pathname: LOGIN_ROUTE });
                return;
            }
            if (isSessionExpired(session)) {
                await logout(true);
                setLoading(false);
                return;
            }
            setLoading(false);
        } catch (e) {
            handleUseAuthErrors(e);
        }
    }, [getSession, handleUseAuthErrors, logout, navigateWithSearch, setLoading]);

    /**
     * Authorizes the user with the onboarding flow based on the provided session.
     * It fetches the user information & validate whether or not user finished onboarding process.
     * If no session then navigate user to login.
     * If session expired then logout the user.
     * If email not verified then force user to verification route.
     * Otherwise force user to appropriate route.
     */
    const authorizeWithOnboardingFlow = useCallback(
        async (orySession?: OrySession) => {
            try {
                let session;
                if (orySession == undefined) {
                    session = await getSession();
                } else {
                    session = orySession;
                }
                if (!session.oryID) {
                    setLoading(false);
                    navigateWithSearch({ pathname: location.pathname == SIGN_UP_ROUTE ? SIGN_UP_ROUTE : LOGIN_ROUTE });
                    return;
                }
                if (isSessionExpired(session)) {
                    await logout(true);
                    setLoading(false);
                    return;
                }
                //TODO: This is a temporary verification skip check for Bacc conference. We agreed upon disable verification check
                // on one specific day.

                if (!getConfig().skipEmailVerification && !session.emailVerified) {
                    setLoading(false);
                    navigateWithRedirectTo({ pathname: VERIFICATION_ROUTE });
                    return;
                }
                if (user && user.id) {
                    return handleUserNavigation(user);
                }
                const mantacareuser = await getCurrentUser(session, handleUseAuthErrors);
                if (!mantacareuser) {
                    return;
                }
                setMantaCareUser(mantacareuser);
                handleUserNavigation(mantacareuser);
                return mantacareuser;
            } catch (e) {
                handleUseAuthErrors(e);
            }
        },
        [handleUseAuthErrors, user, logout, getSession, getCurrentUser, navigateWithSearch, navigateWithRedirectTo, location]
    );

    const handleUserNavigation = (user: MantaCareUser | undefined) => {
        const { pathname, state, search } = location;
        const routeToNavigate = getOnboardingRoute(user, pathname);
        setLoading(false);
        if (routeToNavigate) {
            //navigates to onboarding route with redirection param if onboarding is not done
            navigateWithRedirectTo({ pathname: routeToNavigate }, { state });
            return;
        }

        //For user finished entire onboarding flow
        const params = new URLSearchParams(search);
        const { redirectTo, ...rest } = Object.fromEntries(params.entries());
        if (redirectTo) {
            //navigates to redirectTo url
            navigateWithSearch({ pathname: redirectTo, search: addSearchParams('', rest) }, { state, replace: true });
            return;
        }

        if ((state as Record<string, any>)?.onboarding) {
            //Just finsihed onboarding flow
            navigateWithSearch({ pathname: pathname }, { state, replace: true });
            return;
        }

        if (onboardingRoutes.includes(pathname)) {
            //If user finishes entire onboarding flow and try to access onboarding route navigate to home.
            navigateWithSearch({ pathname: HOME_ROUTE }, { state, replace: true });
            return;
        }

        navigateWithSearch({ pathname }, { state, replace: true });
    };
    /**
     * It acts as a authorization check point. If the user logged is admin, can access users list, user details page.
     */
    const authorizeAdmin = useCallback(async () => {
        setLoading(true);
        if (!user || !user.isAdmin) {
            navigateWithSearch({ pathname: HOME_ROUTE });
            return;
        }
        setLoading(false);
    }, [navigateWithSearch, setLoading]);

    return { authorize, error, authorizeWithOnboardingFlow, loading, authorizeAdmin, getSession, extendUserSession };
};
